import { ClickAwayListener, SwipeableDrawer } from "@mui/material";
import { styled } from "@mui/material/styles";
import DrawerActionBar from "components/drawerActionBar";
import { BlurredBackground } from "components/primitives/BlurredBackground";
import { useDrawerContext } from "contexts/DrawerContext";
import { useOrderSteps } from "hooks/useOrderSteps";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import { type SyntheticEvent, useRef } from "react";
import { selectCurrentOrderStep, selectOrderSummary } from "slices/transactionSlice";
import { ORDER_STEP_1_SIZE_LARGE, ORDER_STEP_1_SIZE_SMALL } from "utils/designTokens";
import { getDrawerHeight } from "./utils";

const IOS_DEVICE_REGEX = /iPad|iPhone|iPod/u;

export const OrderProcessDrawer = () => {
	const orderSummary = useAppSelector(selectOrderSummary);
	const { open, toggleDrawer } = useDrawerContext();
	const currentOrderStepScreen = useOrderSteps({ open: open, toggleDrawer: toggleDrawer });
	const currentOrderStep = useAppSelector(selectCurrentOrderStep);
	const actionBarRef = useRef<HTMLDivElement>(null);
	const drawerRef = useRef<HTMLDivElement | null>(null);
	const iOs = typeof navigator !== "undefined" && IOS_DEVICE_REGEX.test(navigator.userAgent);

	const handleToggleDrawer = (newState: boolean, e?: SyntheticEvent<HTMLElement> | MouseEvent | TouchEvent) => {
		const isTargetHtmlElement = e?.target instanceof HTMLElement;
		const languagePicker = document.getElementById("language-picker");
		if (languagePicker && isTargetHtmlElement && languagePicker.contains(e.target)) {
			return;
		}
		toggleDrawer(newState);
	};

	const getCurrentDrawerHeight = () => getDrawerHeight(open, currentOrderStep, orderSummary);

	return (
		<ClickAwayListener
			onClickAway={(e) => {
				const languagePicker = document.getElementById("language-picker");
				if (languagePicker && e.target instanceof HTMLElement && languagePicker.contains(e.target)) {
					return;
				}

				if (actionBarRef.current?.children) {
					for (const child of actionBarRef.current?.children || []) {
						if (child.contains(e.target as Node)) {
							return;
						}
					}
				}
				if (actionBarRef.current === e.target) {
					return;
				}
				drawerRef.current?.classList.add("test");
				handleToggleDrawer(false, e);
			}}
			touchEvent="onTouchStart"
		>
			<div>
				<ProcessDrawer
					ref={drawerRef}
					anchor="bottom"
					open={open}
					onClose={(e: SyntheticEvent<HTMLElement>) => handleToggleDrawer(false, e)}
					onOpen={(e: SyntheticEvent<HTMLElement>) => {
						handleToggleDrawer(true, e);
						drawerRef.current?.classList.remove("test");
					}}
					disableBackdropTransition={true}
					swipeAreaWidth={currentOrderStep === 1 && open ? ORDER_STEP_1_SIZE_LARGE : ORDER_STEP_1_SIZE_SMALL}
					allowSwipeInChildren={true} // Enables to swipe up in Kiosk mode
					disableSwipeToOpen={false}
					ModalProps={{
						disableAutoFocus: false,
						keepMounted: true,
						hideBackdrop: false,
						sx: {
							"& .MuiBackdrop-root": {
								top: `calc(100% - ${getCurrentDrawerHeight()}px)`,
							},
						},
					}}
					slotProps={{
						backdrop: {
							invisible: true,
						},
					}}
					sx={{
						height: getCurrentDrawerHeight(),
						top: `calc(100% - ${getCurrentDrawerHeight()}px)`,
						"& .MuiDrawer-paper": {
							height: getCurrentDrawerHeight(),
						},

						"&.test .swipeable-area": {
							transition: "none  !important",
						},
					}}
					disableDiscovery={iOs}
					disableAutoFocus={true}
					data-testid="process-drawer"
				>
					<BlurredBackground
						orderStep={currentOrderStep}
						open={open}
						sx={{
							borderRadius: open ? "24px 24px 0 0" : 0,
						}}
					/>
					{currentOrderStepScreen}
				</ProcessDrawer>
				<DrawerActionBar currentOrderStep={currentOrderStep} ref={actionBarRef} />
			</div>
		</ClickAwayListener>
	);
};

const ProcessDrawer = styled(SwipeableDrawer)(() => ({
	bottom: "0px",
	visibility: "visible",
	backgroundColor: "transparent",

	"& .MuiDrawer-paper": {
		padding: "0 40px 0 40px",
		border: 0,
		backgroundColor: "transparent",
	},
}));
